// First-time context prompts (§9.1 step 3): shown in the Lobby, Practice and the menu diorama
// during a player's first 3 sessions (never in online rounds or replays); each fades after 3
// successful uses, which count anywhere. Pure logic: the HUD
// calls `update(view)` every frame and draws the returned PromptView (text + device glyphs).
//
//   const prompts = createFirstPrompts({ bus, profiles, glyph: (a) => input.glyph(a) });
//   const p = prompts.update(view);   // PromptView | null — null: draw nothing (or fade out)
//
// Priority: contextual prompts win while their situation lasts (teetering → "Push back to
// recover!", a ledge-hanger nearby → stomp, holding an item → use it, carrying → throw); otherwise
// the basics in order Shove → Jump → Grab. A prompt stays at least MIN_SHOW_MS once shown.
import { NO_SLOT } from '@shared/constants';
import { EV } from '@shared/events';
import { BS, ItemKind, RoomPhase } from '@shared/ids';
import { onSim, type Bus } from '../core/bus';
import type { FrameView } from '../game/FrameView';
import type { Glyph, GlyphAction } from '../input/glyphs';
import type { ProfileService } from './ProfileService';

export type PromptId = 'shove' | 'grab' | 'jump' | 'item' | 'stomp' | 'recover';
type Part = string | { glyph: GlyphAction };

export interface PromptDef { id: PromptId; parts: readonly Part[]; success: number }

/** §9.1's six prompts; `success` is the local event that counts as a successful use. */
export const FIRST_PROMPTS: readonly PromptDef[] = [
  { id: 'shove', parts: ['Shove: ', { glyph: 'shove' }], success: EV.LUNGE },
  { id: 'grab', parts: ['Grab a bean: ', { glyph: 'grab' }, ' … then throw: ', { glyph: 'throw' }], success: EV.THROW },
  { id: 'jump', parts: ['Jump: ', { glyph: 'jump' }], success: EV.JUMP },
  { id: 'item', parts: ['Use item: ', { glyph: 'item' }], success: EV.ITEM_USE },
  { id: 'stomp', parts: ['Stomp the hands of ledge-hangers!'], success: EV.STOMP },
  { id: 'recover', parts: ['Push back to recover!'], success: EV.TEETER_SAVE },
];

export const PROMPT_USES = 3;
export const PROMPT_SESSIONS = 3;
const MIN_SHOW_MS = 2500;
/** A finished prompt keeps showing this long so the HUD can play its "done" fade. */
const DONE_LINGER_MS = 900;
const STOMP_NEAR_M = 3.5;
const BASICS: readonly PromptId[] = ['shove', 'jump', 'grab'];

export interface PromptView {
  id: PromptId;
  /** Text and glyphs in order, for rich rendering. */
  parts: (string | Glyph)[];
  /** Plain text with glyphs as "[LMB]" / "(A)" / "BONK". */
  text: string;
  uses: number;
  goal: number;
  /** True for the short linger after the 3rd use (fade it out). */
  done: boolean;
}

export interface FirstPrompts {
  /** Within the first 3 sessions and something is left to teach. */
  readonly active: boolean;
  update(view: FrameView, nowMs?: number): PromptView | null;
  dispose(): void;
}

export interface FirstPromptsOptions {
  bus: Bus;
  profiles: Pick<ProfileService, 'get' | 'promptUse'>;
  glyph(action: GlyphAction): Glyph;
  /** Clock in ms (defaults to performance.now); `update(view, nowMs)` may also pass the time. */
  now?(): number;
}

export function createFirstPrompts(opts: FirstPromptsOptions): FirstPrompts {
  const defs = new Map(FIRST_PROMPTS.map((d) => [d.id, d]));
  const clock = opts.now ?? (() => performance.now());
  /** The latest time seen (the caller's frame clock when it passes one). */
  let lastNow = 0;
  let localSlot = NO_SLOT;
  let current: PromptId | null = null;
  let shownAt = 0;
  let doneAt = -1;
  let doneId: PromptId | null = null;

  const off = onSim(opts.bus, (ev, _local, replay) => {
    if (replay || localSlot === NO_SLOT || ev.a !== localSlot || !active()) return;
    for (const d of FIRST_PROMPTS) {
      if (d.success !== ev.type || isDone(d.id)) continue;
      opts.profiles.promptUse(d.id, PROMPT_USES);
      if (isDone(d.id) && current === d.id) {
        doneId = d.id;
        doneAt = lastNow;
      }
    }
  });

  function isDone(id: PromptId): boolean {
    const p = opts.profiles.get();
    return p.firstPromptsDone.includes(id) || (p.promptUses[id] ?? 0) >= PROMPT_USES;
  }

  function active(): boolean {
    const p = opts.profiles.get();
    return p.sessions <= PROMPT_SESSIONS && FIRST_PROMPTS.some((d) => !isDone(d.id));
  }

  function contextual(view: FrameView): PromptId | null {
    const me = view.beans[localSlot];
    if (!me || !me.active || me.ghost || me.out) return null;
    if (me.state === BS.TEETER && !isDone('recover')) return 'recover';
    if (!isDone('stomp')) {
      for (const b of view.beans) {
        if (b.slot !== localSlot && b.active && b.state === BS.LEDGE && Math.hypot(b.x - me.x, b.y - me.y) <= STOMP_NEAR_M) return 'stomp';
      }
    }
    if (me.item !== ItemKind.NONE && me.charges > 0 && !isDone('item')) return 'item';
    if (me.state === BS.CARRYING && !isDone('grab')) return 'grab';
    return null;
  }

  function render(id: PromptId, done: boolean): PromptView {
    const def = defs.get(id)!;
    const parts = def.parts.map((p) => (typeof p === 'string' ? p : opts.glyph(p.glyph)));
    const text = parts.map((p) => (typeof p === 'string' ? p : p.text)).join('');
    return { id, parts, text, uses: Math.min(PROMPT_USES, opts.profiles.get().promptUses[id] ?? 0), goal: PROMPT_USES, done };
  }

  return {
    get active() {
      return active();
    },
    update(view, nowMs = clock()) {
      lastNow = nowMs;
      localSlot = view.localSlot;
      if (doneId !== null) {
        if (nowMs - doneAt < DONE_LINGER_MS) return render(doneId, true);
        doneId = null;
        current = null;
      }
      // §9.1: prompts appear in the menu diorama, Practice (both local) and the online Lobby only.
      const where = view.source === 'local' || (view.source === 'online' && view.roomPhase === RoomPhase.LOBBY);
      if (!active() || localSlot === NO_SLOT || !where) {
        current = null;
        return null;
      }
      const ctx = contextual(view);
      let next: PromptId | null = ctx;
      if (next === null) {
        // Keep a basic prompt for its minimum time, then move to the next unfinished basic.
        if (current !== null && !isDone(current) && nowMs - shownAt < MIN_SHOW_MS) next = current;
        else next = BASICS.find((id) => !isDone(id)) ?? null;
      }
      if (next !== current) {
        current = next;
        shownAt = nowMs;
      }
      return current === null ? null : render(current, false);
    },
    dispose() {
      off();
    },
  };
}
